<script setup>
import Pagination from '@/Components/Pagination/PaginationFE.vue';
import Filters from '@/Components/Table/Filters.vue';
import Table from '@/Components/Table/Table.vue';
import StaffLayout from '@/Layouts/StaffLayout.vue';

import { useForm } from '@inertiajs/vue3';
import { useDebounceFn } from '@vueuse/core';
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const route = inject('route');
const props = defineProps({
    query: Object,
    invoices: Object,
    statusOptions: Object,
});

const form = useForm({
    search: props.query.search ?? '',
    status: props.query.status ?? '',
    orderBy: props.query.orderBy ?? 'due_date',
    orderDir: props.query.orderDir ?? 'desc',
    page: props.query.page ?? 1,
});

const getData = (resetPage = true) => {
    form.page = resetPage ? 1 : form.page;

    form.get(route('staff.invoices.index'), {
        preserveState: true,
        preserveScroll: true,
        only: ['invoices', 'query'],
    });
};

const debouncedGetData = useDebounceFn(() => getData(), 300);

const typeFilters = {
    status: {
        options: { '': t('All {attributes}', { attributes: t('statuses') }), ...props.statusOptions },
    },
};

const tableSettings = [
    { label: t('Name'), orderBy: 'name', width: '35%' },
    { label: t('Invoice number') },
    { label: t('Date'), orderBy: 'date' },
    { label: t('Due date'), orderBy: 'due_date' },
    { label: '', cell: 'Arrow', width: '2%' },
];

const tableData = computed(() =>
    props.invoices.data.map((invoice) => {
        return {
            id: invoice.id, // used as vue key if set
            route: route('staff.invoices.show', invoice.id),
            colorCode: invoice.status == 'concept' ? '#C30000' : '#5C8938',
            rowData: [
                invoice.location_name,
                invoice.number,
                invoice.date,
                invoice.due_date,
                route('staff.invoices.show', invoice.id),
            ],
        };
    })
);

const setPage = (e) => {
    form.page = e;
    getData(false);
};
</script>

<template>
    <StaffLayout :title="$t('Invoices')">
        <Filters
            :form="form"
            @anyUpdate="getData()"
            :h1="$t('Invoices')"
            :typeFilters="typeFilters"
            :hasSearch="true"
            @searchUpdate="debouncedGetData()"
        />

        <template v-if="typeof invoices !== 'undefined' && invoices.data.length > 0">
            <Table
                class="mb-8"
                :loading="form.processing"
                :tableSettings="tableSettings"
                :data="tableData"
                v-model:orderBy="form.orderBy"
                @update:orderBy="getData()"
                v-model:orderDir="form.orderDir"
                @update:orderDir="getData()"
            />

            <Pagination
                v-if="typeof invoices !== 'undefined'"
                :links="invoices.links"
                @setPage="setPage"
                :pageData="invoices"
                :hideTotal="query.search != '' && query.search != null"
            />
        </template>

        <template v-else>
            <div class="w-full px-4 py-3 overflow-hidden font-bold leading-tight bg-white rounded">
                {{ $t('No {model} found', { model: $t('invoices') }) }}
            </div>
        </template>
    </StaffLayout>
</template>
